import { findBestTransfer } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import equal from 'fast-deep-equal'
import { generateTransferCheckoutItem } from 'lib/checkout/experiences/cart'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getIncludedTransferzExtras } from './transferExtras'
import {
  getTransferzOneClickTopPick,
  isTransferQuoteExpired,
  type TransferzOneClickQuote,
} from 'lib/experiences/transferQuoteUtils'

type DefaultTransfers = {
  toHotel?: App.ExperienceTransferView
  toAirport?: App.ExperienceTransferView
}
type BestOptions = {
  bestToHotelOption?: App.ExperienceTransferOption | undefined
  bestToAirportOption?: App.ExperienceTransferOption | undefined
}

export interface OneClickTransferQuotes {
  toHotel: Array<TransferzOneClickQuote>
  toAirport: Array<TransferzOneClickQuote>
}

interface OneClickTransferCartChange {
  type: 'add' | 'update'
  item: App.Checkout.TransferItem
}

export function getOneClickTransferCartChanges(
  currentItems: ReadonlyArray<App.Checkout.TransferItem>,
  preparedItems: ReadonlyArray<App.Checkout.TransferItem>,
): { changes: Array<OneClickTransferCartChange>, removedIds: Array<string> } {
  // Preserve identity when changing a leg and keep both legs in the same journey.
  const journeyId = currentItems[0]?.metaData?.transferzJourneyId ?? preparedItems[0]?.metaData?.transferzJourneyId
  const updatedItems = preparedItems.map(item => {
    const existing = currentItems.find(current => current.transfer.type === item.transfer.type)
    if (!existing) {
      return {
        ...item,
        metaData: { ...item.metaData, transferzJourneyId: journeyId },
      }
    }

    return {
      ...existing,
      transfer: item.transfer,
      total: item.total,
      metaData: { ...existing.metaData, ...item.metaData, transferzJourneyId: journeyId },
    }
  })

  const changes = updatedItems.flatMap<OneClickTransferCartChange>(item => {
    const existing = currentItems.find(current => current.itemId === item.itemId)
    if (!existing) return [{ type: 'add', item }]
    if (!equal(existing, item)) return [{ type: 'update', item }]
    return []
  })
  const removedIds = currentItems
    .filter(item => !updatedItems.some(updated => updated.itemId === item.itemId))
    .map(item => item.itemId)

  return { changes, removedIds }
}

export function hasSameTransferTravellerCounts(
  left: App.ExperienceTransferTravellers,
  right: App.ExperienceTransferTravellers,
): boolean {
  return left.adults === right.adults &&
    (left.children ?? 0) === (right.children ?? 0) &&
    (left.infants ?? 0) === (right.infants ?? 0)
}

export function getBestOneClickTransferOptions(
  travellers: App.ExperienceTransferTravellers,
  defaults: DefaultTransfers,
  options: {
    toHotelOptions: Array<App.ExperienceTransferOption>
    toAirportOptions: Array<App.ExperienceTransferOption>
  },
  quotes?: OneClickTransferQuotes,
): BestOptions {
  if (quotes) {
    const quotedTravellers =
      defaults.toHotel?.travellers ?? defaults.toAirport?.travellers
    if (
      !quotedTravellers ||
      !hasSameTransferTravellerCounts(travellers, quotedTravellers)
    ) {
      return {}
    }

    return {
      bestToHotelOption: defaults.toHotel
        ? getTransferzOneClickTopPick(
          quotes.toHotel,
          { ...defaults.toHotel, travellers },
        )?.option
        : undefined,
      bestToAirportOption: defaults.toAirport
        ? getTransferzOneClickTopPick(
          quotes.toAirport,
          { ...defaults.toAirport, travellers },
        )?.option
        : undefined,
    }
  }

  return {
    bestToHotelOption: defaults.toHotel
      ? findBestTransfer(
        { ...defaults.toHotel, travellers },
        options.toHotelOptions,
      ).bestTransfer
      : undefined,
    bestToAirportOption: defaults.toAirport
      ? findBestTransfer(
        { ...defaults.toAirport, travellers },
        options.toAirportOptions,
      ).bestTransfer
      : undefined,
  }
}

export function generateOneClickTransferCheckoutItems(
  experience: App.ExperienceOffer,
  transfers: {
    toHotel?: App.ExperienceTransferView
    toAirport?: App.ExperienceTransferView
  },
  transferType: App.ExperienceTransferWithRoundTripType,
  quotes?: OneClickTransferQuotes,
): Array<App.Checkout.TransferItem> {
  const selectedTransfers = [
    transfers.toHotel,
    transfers.toAirport,
  ].filter((transfer): transfer is App.ExperienceTransferView =>
    !!transfer &&
    (transferType === 'ROUND-TRIP' || transfer.type === transferType),
  )
  if (!selectedTransfers.length) return []

  const journeyId = uuidV4()
  const items = selectedTransfers.map(transfer => {
    const baseItem: App.Checkout.TransferItem = {
      ...generateTransferCheckoutItem(experience, transfer),
      origin: 'oneclick',
    }
    if (!quotes) return baseItem

    const directionQuotes = transfer.type === 'AIRPORT-TO-HOTEL'
      ? quotes.toHotel
      : quotes.toAirport
    const quote = directionQuotes.find(
      candidate => candidate.option === transfer.option,
    )
    if (!quote) return undefined

    const item: App.Checkout.TransferItem = {
      ...baseItem,
      transfer: {
        ...transfer,
        title: quote.ticket.name,
        pickupPoint: quote.option.pickupPoint ?? transfer.pickupPoint,
        dropoffPoint: quote.option.dropoffPoint ?? transfer.dropoffPoint,
        extras: getIncludedTransferzExtras(quote.option.extras),
      },
      total: quote.ticket.price,
      metaData: {
        transferzJourneyId: journeyId,
        quoteExpiresAt: quote.expiresAt,
        ...(quote.ticket.providerData && {
          providerBookingData: quote.ticket.providerData,
        }),
      },
    }

    return isTransferQuoteExpired(item) ? undefined : item
  })

  if (items.some(item => !item)) return []

  return items.filter(
    (item): item is App.Checkout.TransferItem => !!item,
  )
}

export function getOneClickTransfersItems(transfer: App.ExperienceBookingView, travellers: App.ExperienceTransferTravellers, defaultTransfers: DefaultTransfers, bestOptions: BestOptions, specialRequest: string) {
  const airportTransfer = defaultTransfers.toAirport
  const hotelTransfer = defaultTransfers.toHotel

  const purchaseToHotel: App.ExperienceTransferView | undefined = hotelTransfer ? {
    ...hotelTransfer,
    travellers: {
      ...travellers,
      // if we have no children, reset the seats. We only kept them around in case the user
      // flicked back and forth between having children and not for convenience
      boosterSeats: travellers.children ? travellers.boosterSeats : 0,
      childSeats: travellers.children ? travellers.childSeats : 0,
    },
    type: hotelTransfer.type,
    flightNumber: hotelTransfer.flightNumber || '',
    time: hotelTransfer.time || '',
    date: hotelTransfer.date,
    option: bestOptions.bestToHotelOption,
    title: transfer.title,
    specialRequests: specialRequest,
  } : undefined

  const purchaseToAirport: App.ExperienceTransferView | undefined = airportTransfer ? {
    ...airportTransfer,
    travellers: {
      ...travellers,
      // if we have no children, reset the seats. We only kept them around in case the user
      // flicked back and forth between having children and not for convenience
      boosterSeats: travellers.children ? travellers.boosterSeats : 0,
      childSeats: travellers.children ? travellers.childSeats : 0,
    },
    type: airportTransfer.type,
    flightNumber: airportTransfer.flightNumber || '',
    time: airportTransfer.time || '',
    date: airportTransfer.date,
    option: bestOptions.bestToAirportOption,
    title: transfer.title,
    specialRequests: specialRequest,
  } : undefined

  const result = { transfers: [purchaseToHotel, purchaseToAirport].filter(Boolean) }

  const toHotel = result.transfers.find(transfer => transfer?.type === 'AIRPORT-TO-HOTEL')
  const toAirport = result.transfers.find(transfer => transfer?.type === 'HOTEL-TO-AIRPORT')

  return {
    toHotel,
    toAirport,
  }
}
